import React, { useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from './firebase';

const COMMISSION_RATE = 0.30;

export default function EarningsTab({ pharmacyId, pharmacyData, orders }) {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');

  // Real automated payouts, written by the backend's daily 8pm job. The
  // admin-only pharmacyPayouts/{date}/{pharmacyId} tree can't be read by a
  // pharmacy account directly (it's keyed by date, not owner, so there's no
  // way to scope a rule to "your own rows" there) — this reads the
  // by-pharmacy mirror instead, which carries the exact same payout records.
  useEffect(() => {
    const unsubscribe = onValue(ref(db, `pharmacyPayoutsByPharmacy/${pharmacyId}`), snap => {
      const val = snap.val() || {};
      const mine = Object.entries(val)
        .map(([date, payout]) => ({ date, ...payout }))
        .sort((a, b) => new Date(b.processedAt) - new Date(a.processedAt));
      setPayouts(mine);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [pharmacyId]);

  // Countdown to the next daily 8pm GMT payout run.
  const now = new Date();
  const nextPayout = new Date(now);
  nextPayout.setHours(20, 0, 0, 0);
  if (now >= nextPayout) nextPayout.setDate(nextPayout.getDate() + 1);
  const hoursLeft = Math.max(1, Math.ceil((nextPayout - now) / (1000 * 60 * 60)));
  const isToday = nextPayout.toDateString() === now.toDateString();

  const todayStr  = now.toDateString();
  const weekAgo   = new Date(now - 7 * 24 * 60 * 60 * 1000);
  const monthAgo  = new Date(now - 30 * 24 * 60 * 60 * 1000);

  const delivered = orders.filter(o => o.status === 'delivered');

  const filterOrders = (list) => {
    if (period === 'today') return list.filter(o => new Date(o.createdAt).toDateString() === todayStr);
    if (period === 'week')  return list.filter(o => new Date(o.createdAt) >= weekAgo);
    if (period === 'month') return list.filter(o => new Date(o.createdAt) >= monthAgo);
    return list;
  };

  const periodOrders = filterOrders(delivered);

  // Your real base price (100% yours) — falls back to deriving it from a
  // customer-facing total only for legacy orders that predate
  // pharmacySubtotal being recorded, so old data doesn't get double-counted.
  const getPharmacyBase = (o) => {
    if (o.pharmacySubtotal != null) return o.pharmacySubtotal;
    const customerFacing = o.customerSubtotal != null ? o.customerSubtotal : (o.total - (o.deliveryFee || 0) - (o.serviceFee || 0));
    return (customerFacing || 0) / (1 + COMMISSION_RATE);
  };

  const totalRevenue = periodOrders.reduce((s, o) => s + (o.total || 0), 0);
  const myEarnings   = periodOrders.reduce((s, o) => s + getPharmacyBase(o), 0);
  const commission   = myEarnings * COMMISSION_RATE;
  const totalPaid = payouts.reduce((s, p) => s + (p.status !== 'FAILED' ? (p.amount || 0) : 0), 0);

  const periodLabel = period === 'today' ? 'Today' : period === 'week' ? 'This Week' : period === 'month' ? 'This Month' : 'All Time';

  const payoutStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'success') return { bg: '#DCFCE7', color: '#15803D', label: '✅ Paid' };
    if (s === 'failed')  return { bg: '#FEE2E2', color: '#B91C1C', label: '❌ Failed' };
    return { bg: '#FEF3C7', color: '#92400E', label: status || 'Pending' };
  };

  return (
    <div>
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontWeight: 700, margin: 0, fontSize: 18 }}>💰 Earnings & Payouts</h2>
          <p style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}>Your revenue, commission breakdown, and payout history</p>
        </div>
        <div style={{ display: 'flex', backgroundColor: '#F1F3F5', borderRadius: 10, padding: 4 }}>
          {[{ id: 'today', label: 'Today' }, { id: 'week', label: 'Week' }, { id: 'month', label: 'Month' }, { id: 'all', label: 'All Time' }].map(p => (
            <button key={p.id} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: period === p.id ? 700 : 400, backgroundColor: period === p.id ? '#FFF' : 'transparent', color: period === p.id ? '#1D9E75' : '#888' }} onClick={() => setPeriod(p.id)}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* NEXT AUTO-PAYOUT BANNER */}
      <div style={{ backgroundColor: '#1D9E75', borderRadius: 12, padding: 20, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <p style={{ color: '#A8EDCF', fontSize: 12, margin: '0 0 4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            🤖 Next Automatic Payout
          </p>
          <p style={{ color: '#FFF', fontWeight: 800, fontSize: 18, margin: '0 0 4px' }}>
            {isToday ? 'Today' : 'Tomorrow'} by 8:00 PM
          </p>
          <p style={{ color: '#A8EDCF', fontSize: 13, margin: 0 }}>
            in about {hoursLeft} hour{hoursLeft !== 1 ? 's' : ''} · sent automatically via Paystack Transfer, no action needed
          </p>
        </div>
        <div style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '12px 20px', textAlign: 'center' }}>
          <p style={{ color: '#FFF', fontSize: 28, fontWeight: 800, margin: 0 }}>GH₵ {myEarnings.toFixed(2)}</p>
          <p style={{ color: '#A8EDCF', fontSize: 12, margin: 0 }}>Earned {periodLabel.toLowerCase()}</p>
        </div>
      </div>

      {/* HOW PAYOUT WORKS */}
      <div style={{ backgroundColor: '#EFF6FF', borderRadius: 12, padding: 16, marginBottom: 20, border: '1px solid #BFDBFE' }}>
        <p style={{ fontWeight: 700, fontSize: 14, color: '#1D4ED8', margin: '0 0 10px' }}>ℹ️ How automatic payouts work</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {[
            { icon: '📊', title: 'You set your price', sub: 'You receive 100% of the price you list — PharmDev adds 30% on top, charged to the customer separately' },
            { icon: '🏦', title: 'Sent every day by 8 PM', sub: 'PharmDev automatically totals your delivered orders and sends your full share to your registered MoMo or bank account, every single day' },
            { icon: '📱', title: 'Real transfer, every time', sub: 'Each entry below is an actual Paystack transfer, including a reference you can trace — not just a database note' },
          ].map((item, i) => (
            <div key={i} style={{ backgroundColor: '#FFF', borderRadius: 8, padding: 12, border: '1px solid #BFDBFE' }}>
              <p style={{ fontSize: 22, margin: '0 0 6px' }}>{item.icon}</p>
              <p style={{ fontWeight: 700, fontSize: 13, color: '#1D4ED8', margin: '0 0 4px' }}>{item.title}</p>
              <p style={{ fontSize: 12, color: '#555', margin: 0, lineHeight: 1.5 }}>{item.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* EARNINGS SUMMARY CARDS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
        {[
          { label: 'Total Orders', value: periodOrders.length, color: '#1D9E75', icon: '📦', sub: periodLabel },
          { label: 'Gross Revenue', value: `GH₵ ${totalRevenue.toFixed(2)}`, color: '#3B82F6', icon: '💰', sub: 'Total inc. delivery' },
          { label: 'Your Earnings (100%)', value: `GH₵ ${myEarnings.toFixed(2)}`, color: '#1D9E75', icon: '🏦', sub: 'Your listed prices' },
          { label: 'PharmDev Commission', value: `GH₵ ${commission.toFixed(2)}`, color: '#7C3AED', icon: '📋', sub: '30% added for the customer' },
        ].map((st, i) => (
          <div key={i} style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 18, border: '1px solid #E5E7EB', borderTop: `4px solid ${st.color}`, textAlign: 'center' }}>
            <p style={{ fontSize: 24, margin: '0 0 8px' }}>{st.icon}</p>
            <p style={{ fontSize: 20, fontWeight: 800, color: st.color, margin: '0 0 4px' }}>{st.value}</p>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', margin: '0 0 2px' }}>{st.label}</p>
            <p style={{ fontSize: 11, color: '#888', margin: 0 }}>{st.sub}</p>
          </div>
        ))}
      </div>

      {/* MONEY SPLIT */}
      <div style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 20, border: '1px solid #E5E7EB', marginBottom: 20 }}>
        <h3 style={{ fontWeight: 700, fontSize: 15, margin: '0 0 14px' }}>💡 Revenue Split — {periodLabel}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ backgroundColor: '#E8F8F2', borderRadius: 10, padding: 14, border: '1px solid #B2DFD0' }}>
            <p style={{ fontWeight: 700, color: '#1D6B52', margin: '0 0 10px', fontSize: 13 }}>📥 Your Share (100% of your price)</p>
            <p style={{ fontSize: 26, fontWeight: 800, color: '#1D9E75', margin: '0 0 4px' }}>GH₵ {myEarnings.toFixed(2)}</p>
            <p style={{ fontSize: 12, color: '#555', margin: 0 }}>Every medicine price you set, in full</p>
          </div>
          <div style={{ backgroundColor: '#EDE9FE', borderRadius: 10, padding: 14, border: '1px solid #C4B5FD' }}>
            <p style={{ fontWeight: 700, color: '#5B21B6', margin: '0 0 10px', fontSize: 13 }}>📤 PharmDev Commission</p>
            <p style={{ fontSize: 26, fontWeight: 800, color: '#7C3AED', margin: '0 0 4px' }}>GH₵ {commission.toFixed(2)}</p>
            <p style={{ fontSize: 12, color: '#555', margin: 0 }}>
              30% added on top of your price — the customer pays this, not you
            </p>
          </div>
        </div>
      </div>

      {/* PAYOUT HISTORY */}
      <div style={{ backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ fontWeight: 700, fontSize: 15, margin: 0 }}>📜 Payout History ({payouts.length})</p>
          {totalPaid > 0 && (
            <span style={{ fontSize: 13, color: '#1D9E75', fontWeight: 700 }}>
              Total received: GH₵ {totalPaid.toFixed(2)}
            </span>
          )}
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading payouts...</div>
        ) : payouts.length === 0 ? (
          <div style={{ padding: 50, textAlign: 'center' }}>
            <p style={{ fontSize: 40, marginBottom: 12 }}>💰</p>
            <p style={{ fontWeight: 600, color: '#333', margin: '0 0 6px' }}>No payouts yet</p>
            <p style={{ fontSize: 13, color: '#888', margin: '0 0 16px' }}>
              Your first automatic payout will appear here by <strong>8 PM</strong> after your first delivered order.
            </p>
            <div style={{ backgroundColor: '#E8F8F2', borderRadius: 10, padding: 16, maxWidth: 420, margin: '0 auto', border: '1px solid #B2DFD0' }}>
              <p style={{ fontSize: 13, color: '#1D6B52', margin: 0, lineHeight: 1.6 }}>
                📅 <strong>How it works:</strong> Every day by 8 PM, PharmDev totals your delivered orders and sends your full share to your registered MoMo or bank account via Paystack Transfer. Make sure your payment account is set up under Bank Account.
              </p>
            </div>
          </div>
        ) : (
          <div>
            {payouts.map((payout, i) => {
              const badge = payoutStatusBadge(payout.status);
              return (
                <div key={`${payout.date}-${i}`} style={{ padding: '16px 20px', borderBottom: i < payouts.length - 1 ? '1px solid #F3F4F6' : 'none', backgroundColor: i % 2 === 0 ? '#FFF' : '#FAFAFA' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: '#E8F8F2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                        🤖
                      </div>
                      <div>
                        <p style={{ fontWeight: 700, fontSize: 14, margin: '0 0 2px', color: '#1A1A1A' }}>
                          {payout.date} — Automatic Daily Payout
                        </p>
                        <p style={{ fontSize: 12, color: '#888', margin: 0 }}>
                          {payout.processedAt ? new Date(payout.processedAt).toLocaleString('en-GH', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }) : payout.date}
                        </p>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: 22, fontWeight: 800, color: '#1D9E75', margin: 0 }}>
                        GH₵ {(payout.amount || 0).toFixed(2)}
                      </p>
                      <span style={{ fontSize: 11, backgroundColor: badge.bg, color: badge.color, padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>
                        {badge.label}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {payout.network && (
                      <span style={{ fontSize: 11, color: '#888' }}>💳 Sent to: {payout.network.toUpperCase()}</span>
                    )}
                    {payout.transferCode && (
                      <span style={{ fontSize: 11, color: '#888' }}>🔖 Reference: {payout.transferCode}</span>
                    )}
                    {payout.error && (
                      <span style={{ fontSize: 11, color: '#B91C1C' }}>⚠️ {payout.error}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
